import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Accessibility, Type, Contrast, Link2, PauseCircle,
  MousePointerClick, RotateCcw, X, Eye
} from "lucide-react";

const FEATURES = [
  { key: "a11y-large-font", label: "Large Font", icon: <Type className="w-4 h-4" />, group: "font" },
  { key: "a11y-xl-font", label: "Extra Large Font", icon: <Type className="w-4 h-4" />, group: "font" },
  { key: "a11y-high-contrast", label: "High Contrast", icon: <Contrast className="w-4 h-4" />, group: "visual" },
  { key: "a11y-grayscale", label: "Grayscale", icon: <Eye className="w-4 h-4" />, group: "visual" },
  { key: "a11y-underline-links", label: "Underline Links", icon: <Link2 className="w-4 h-4" />, group: "read" },
  { key: "a11y-dyslexia-font", label: "Readable Font", icon: <MousePointerClick className="w-4 h-4" />, group: "read" },
  { key: "a11y-pause-animations", label: "Pause Animations", icon: <PauseCircle className="w-4 h-4" />, group: "motion" },
];

const STORAGE_KEY = "dbe_a11y_prefs_v1";

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState([]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (Array.isArray(saved)) setActive(saved);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    FEATURES.forEach((f) => html.classList.toggle(f.key, active.includes(f.key)));
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(active)); } catch { /* ignore */ }
  }, [active]);

  const toggle = (key) => {
    setActive((prev) => {
      const groupKeys = FEATURES.filter((f) => f.group === FEATURES.find((x) => x.key === key).group).map((f) => f.key);
      const isActive = prev.includes(key);
      let next = prev.filter((k) => !groupKeys.includes(k));
      if (!isActive) next.push(key);
      return next;
    });
  };

  const reset = () => setActive([]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="a11y-widget-toggle"
        aria-label="Open accessibility tools"
        aria-expanded={open}
        className="fixed z-[60] bottom-24 md:bottom-5 left-5 w-14 h-14 rounded-full bg-[#1C3AA9] hover:bg-[#172f85] text-white shadow-[0_20px_40px_-10px_rgba(28,58,169,0.6)] flex items-center justify-center focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--dbe-red)] transition-transform hover:scale-105"
      >
        <Accessibility className="w-7 h-7" strokeWidth={2.2} />
        <span className="sr-only">Accessibility Menu</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Accessibility Tools"
          data-testid="a11y-widget-panel"
          className="fixed z-[61] bottom-44 md:bottom-24 left-5 w-[min(360px,calc(100vw-2.5rem))] bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)] border border-black/5 overflow-hidden"
        >
          <div className="bg-[#1C3AA9] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5" />
              <span className="font-bold tracking-wide">Accessibility Tools</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="a11y-widget-close"
              aria-label="Close accessibility tools"
              className="p-1 rounded hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 grid grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
            {FEATURES.map((f) => {
              const isOn = active.includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => toggle(f.key)}
                  data-testid={`a11y-btn-${f.key}`}
                  aria-pressed={isOn}
                  className={`flex flex-col items-start gap-1.5 p-3 rounded-xl border text-left transition-all ${
                    isOn
                      ? "bg-[#1C3AA9] border-[#1C3AA9] text-white"
                      : "bg-neutral-50 border-black/10 text-neutral-900 hover:border-[#1C3AA9]"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isOn ? "bg-white/15" : "bg-[#1C3AA9]/10 text-[#1C3AA9]"}`}>
                    {f.icon}
                  </div>
                  <span className="text-xs font-semibold leading-tight">{f.label}</span>
                </button>
              );
            })}
          </div>

          <div className="px-4 pb-4 pt-1 flex items-center justify-between gap-2 border-t border-black/5">
            <button
              type="button"
              onClick={reset}
              data-testid="a11y-reset-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-700 hover:text-[color:var(--dbe-red)]"
            >
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <Link
              to="/accessibility-statement"
              onClick={() => setOpen(false)}
              data-testid="a11y-statement-link"
              className="text-sm font-semibold text-[#1C3AA9] hover:underline"
            >
              Full Accessibility Statement →
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
